import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AxiosInstance from "../Home/AxiosInstance";
import AdminHeader from "./AdminHeader";
import Footer from "../Home/Footer";
import { FiTrash, FiEdit } from "react-icons/fi";
import {
  useTable,
  useSortBy,
  useFilters,
  useResizeColumns,
  usePagination,
} from "react-table";
import "bootstrap/dist/css/bootstrap.min.css";
import "./CompanyDetails.css";
import "./employerPortal.css";

export default function CompanyDetails() {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine if this is for an institution or a company
  const isInstitution = location.pathname.includes("institution");

  const [companyList, setCompanyList] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [companyNameSearch, setCompanyNameSearch] = useState("");
  const [emailSearch, setEmailSearch] = useState("");
  const [phoneNoSearch, setPhoneNoSearch] = useState("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editableCompany, setEditableCompany] = useState(null);
  const [businessTypes, setBusinessTypes] = useState([]);

  useEffect(() => {
    const fetchBusinessTypes = async () => {
      try {
        const response = await AxiosInstance.get("companies/business/");
        setBusinessTypes(response.data);
      } catch (error) {
        console.error("Error fetching business types:", error);
      }
    };

    fetchBusinessTypes();
  }, []);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    const fetchCompanies = async () => {
      try {
        const response = await AxiosInstance.get(
          `companies/company/details/${userId}/`
        );
        setCompanyList(response.data);
        setFilteredCompanies(response.data);
      } catch (error) {
        console.error("Error fetching companies:", error);
      }
    };

    fetchCompanies();
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: isInstitution ? "Institution Name" : "Company Name",
        accessor: "company_name",
        Cell: ({ row }) => (
          <span
            className="text-primary text-decoration-underline"
            style={{ cursor: "pointer" }}
            onClick={() => {
              navigate(
                isInstitution
                  ? `/institution-profile/${row.original.company_id}`
                  : `/company-profile/${row.original.company_id}`,
                {
                  state: {
                    from: isInstitution
                      ? "/institution-details"
                      : "/company-details",
                  },
                }
              );
            }}
          >
            {row.values.company_name}
          </span>
        ),
      },
      {
        Header: "Logo",
        accessor: "company_logo",
        Cell: ({ value }) => {
          if (!value) return <span className="text-muted">—</span>;
          if (value instanceof File) {
            return (
              <img
                src={URL.createObjectURL(value)}
                alt=""
                style={{ width: 40, height: 40, objectFit: "contain" }}
              />
            );
          }
          const path = String(value).replace(/\\/g, "/");
          const src = path.startsWith("http")
            ? path
            : `${AxiosInstance.defaults.baseURL}media/${path}`;
          return (
            <img
              src={src}
              alt=""
              style={{ width: 40, height: 40, objectFit: "contain" }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          );
        },
      },
      { Header: "Registration Number", accessor: "registration_number" },
      { Header: "Phone No 1", accessor: "phone_no_1" },
      { Header: "Phone No 2", accessor: "phone_no_2" },
      { Header: "Email", accessor: "email" },
      { Header: "Business Name", accessor: "business_type.business_type" },
      { Header: "Website", accessor: "website" },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => handleEdit(row.original)}
            >
              <FiEdit />
            </button>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleDeleteClick(row.original.company_id)}
            >
              <FiTrash />
            </button>
          </div>
        ),
      },
    ],
    [navigate, isInstitution]
  );

  const data = useMemo(() => filteredCompanies, [filteredCompanies]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageSize: 10 },
    },
    useFilters,
    useSortBy,
    usePagination,
    useResizeColumns
  );

  const handleDeleteClick = async (companyId) => {
    if (!companyId) return;
    const isConfirmed = window.confirm(
      `Are you sure you want to delete this ${
        isInstitution ? "institution" : "company"
      }?`
    );
    if (isConfirmed) {
      try {
        await AxiosInstance.delete(`companies/company/delete/${companyId}/`);
        const updatedCompanies = companyList.filter(
          (company) => company.company_id !== companyId
        );
        setCompanyList(updatedCompanies);
        setFilteredCompanies(updatedCompanies);
      } catch (error) {
        console.error("Error deleting company:", error);
      }
    }
  };

  const handleEdit = (company) => {
    // Extract business_type_id from business_type object if it exists
    const businessTypeId = company.business_type?.business_type_id 
      ? company.business_type.business_type_id 
      : company.business_type_id || "";
    
    setEditableCompany({
      ...company,
      business_type_id: businessTypeId,
    });
    setIsEditModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const formData = new FormData();

      formData.append("company_name", editableCompany.company_name);
      formData.append(
        "registration_number",
        editableCompany.registration_number
      );
      formData.append("email", editableCompany.email);
      formData.append("phone_no_1", editableCompany.phone_no_1);
      const phone2 = editableCompany.phone_no_2;
      formData.append("phone_no_2", phone2 ? parseInt(phone2) : "");
      formData.append("website", editableCompany.website);
      if (editableCompany.business_type_id) {
        formData.append(
          "business_type",
          parseInt(editableCompany.business_type_id)
        );
      }

      if (editableCompany.company_logo instanceof File) {
        formData.append("company_logo", editableCompany.company_logo);
      }

      await AxiosInstance.put(
        `companies/company/update/${editableCompany.company_id}/`,
        formData
      );

      // Refetch the company data to get the updated business_type object from server
      const userId = localStorage.getItem("userId");
      const response = await AxiosInstance.get(
        `companies/company/details/${userId}/`
      );
      
      setCompanyList(response.data);
      setFilteredCompanies(response.data);
      setIsEditModalOpen(false);
    } catch (error) {
      console.error("Error saving company:", error);
    }
  };

  const handleAddCompanyClick = () =>
    navigate(isInstitution ? "/add-institution" : "/add-company");

  const handleSearch = () => {
    const filtered = companyList.filter(
      (company) =>
        company.company_name
          ?.toLowerCase()
          .includes(companyNameSearch.toLowerCase()) &&
        company.email?.toLowerCase().includes(emailSearch.toLowerCase()) &&
        company.phone_no_1.toString().includes(phoneNoSearch)
    );
    setFilteredCompanies(filtered);
  };

  useEffect(() => {
    handleSearch();
  }, [companyNameSearch, emailSearch, phoneNoSearch]);

  return (
    <div className="CompanyPage als-employer-page">
      <AdminHeader />
      <div className="company-container py-4">
        <h2 className="section-heading">
          List of {isInstitution ? "Institutions" : "Companies"}
        </h2>

        {/* Search Section */}
        <div className="card-custom">
          <div className="row g-3 mb-3">
            <div className="col-md-3">
              <input
                type="text"
                className="form-control"
                placeholder={`Search by ${
                  isInstitution ? "Institution" : "Company"
                } Name`}
                value={companyNameSearch}
                onChange={(e) => setCompanyNameSearch(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <input
                type="text"
                className="form-control"
                placeholder="Search by Email"
                value={emailSearch}
                onChange={(e) => setEmailSearch(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <input
                type="text"
                className="form-control"
                placeholder="Search by Phone No"
                value={phoneNoSearch}
                onChange={(e) => setPhoneNoSearch(e.target.value)}
              />
            </div>
            <div className="col-md-3 d-grid">
              <button
                className="btn btn-primary"
                onClick={handleAddCompanyClick}
              >
                Add {isInstitution ? "Institution" : "Company"}
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <table
              className="table table-bordered table-striped"
              {...getTableProps()}
            >
              <thead className="table-light">
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                      >
                        {column.render("Header")}
                        {column.isSorted
                          ? column.isSortedDesc
                            ? " 🔽"
                            : " 🔼"
                          : ""}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {filteredCompanies.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="text-center">
                      No {isInstitution ? "institutions" : "companies"} found
                    </td>
                  </tr>
                ) : (
                  page.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td {...cell.getCellProps()}>
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination d-flex align-items-center gap-2 mt-3">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
            >
              Previous
            </button>
            <span>
              Page <strong>{pageIndex + 1}</strong> of{" "}
              {pageOptions.length || 1}
            </span>
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => nextPage()}
              disabled={!canNextPage}
            >
              Next
            </button>
          </div>

          {/* Edit Modal */}
          {isEditModalOpen && editableCompany && (
            <div
              className="modal fade show d-block"
              tabIndex="-1"
              style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
            >
              <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      Edit {isInstitution ? "Institution" : "Company"} Details
                    </h5>
                  </div>

                  <div className="modal-body">
                    <form>
                      <div className="mb-3">
                        <label className="form-label">
                          {isInstitution ? "Institution" : "Company"} Name
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editableCompany.company_name}
                          onChange={(e) =>
                            setEditableCompany({
                              ...editableCompany,
                              company_name: e.target.value,
                            })
                          }
                        />
                      </div>

                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            Registration Number
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={editableCompany.registration_number}
                            onChange={(e) =>
                              setEditableCompany({
                                ...editableCompany,
                                registration_number: e.target.value,
                              })
                            }
                          />
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="form-label">Email</label>
                          <input
                            type="email"
                            className="form-control"
                            value={editableCompany.email}
                            onChange={(e) =>
                              setEditableCompany({
                                ...editableCompany,
                                email: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Phone No 1</label>
                          <input
                            type="text"
                            className="form-control"
                            value={editableCompany.phone_no_1}
                            onChange={(e) =>
                              setEditableCompany({
                                ...editableCompany,
                                phone_no_1: e.target.value,
                              })
                            }
                          />
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="form-label">Phone No 2</label>
                          <input
                            type="text"
                            className="form-control"
                            value={editableCompany.phone_no_2}
                            onChange={(e) =>
                              setEditableCompany({
                                ...editableCompany,
                                phone_no_2: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Business Type</label>
                          <select
                            className="form-select"
                            value={editableCompany.business_type_id ? String(editableCompany.business_type_id) : ""}
                            onChange={(e) =>
                              setEditableCompany({
                                ...editableCompany,
                                business_type_id: e.target.value,
                              })
                            }
                          >
                            <option value="">Select Business Type</option>
                            {businessTypes.map((b) => (
                              <option
                                key={b.business_type_id}
                                value={String(b.business_type_id)}
                              >
                                {b.business_type}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="form-label">Website</label>
                          <input
                            type="text"
                            className="form-control"
                            value={editableCompany.website}
                            onChange={(e) =>
                              setEditableCompany({
                                ...editableCompany,
                                website: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Logo</label>
                        {(editableCompany.company_logo instanceof File ||
                          (editableCompany.company_logo &&
                            typeof editableCompany.company_logo ===
                              "string")) && (
                          <div className="mb-2">
                            <img
                              src={
                                editableCompany.company_logo instanceof File
                                  ? URL.createObjectURL(
                                      editableCompany.company_logo
                                    )
                                  : String(editableCompany.company_logo).startsWith(
                                      "http"
                                    )
                                  ? editableCompany.company_logo
                                  : `${
                                      AxiosInstance.defaults.baseURL
                                    }media/${String(
                                      editableCompany.company_logo
                                    ).replace(/\\/g, "/")}`
                              }
                              alt="Logo preview"
                              style={{
                                width: 72,
                                height: 72,
                                objectFit: "contain",
                                border: "1px solid #e5e7eb",
                                borderRadius: 8,
                                padding: 4,
                                background: "#fff",
                              }}
                            />
                          </div>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          className="form-control"
                          onChange={(e) =>
                            setEditableCompany({
                              ...editableCompany,
                              company_logo: e.target.files[0],
                            })
                          }
                        />
                      </div>
                    </form>
                  </div>

                  <div className="modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setIsEditModalOpen(false)}
                    >
                      Cancel
                    </button>
                    <button className="btn btn-primary" onClick={handleSave}>
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
